/**
 * majlis.ts — «المجلس» / Iwan money-luxury derivation layer.
 *
 * Pure functions that derive the Majlis palette from a tenant brand hex. This is
 * the web port of the mobile TRUTH SOURCE `mobile/lib/app/theme/hsl_brand.dart`
 * (class `MajlisPalette`); every recipe + clamp mirrors that file so the two
 * clients render the same money panel + domain chips for a given brand.
 *
 * The recipe is HSL-based (matching Flutter's `HSLColor`) — verified to agree
 * with culori's `hsl` model to the exact byte on the default terracotta swatch
 * set, so the mobile pinned hexes reproduce here without re-tuning:
 *   #C85C2E → pay #1B5038 / payDeep #143D2B / leaves-fg #297A6A /
 *             overtime-fg #7A5A29 / expense-fg #7C4B74.
 *
 * NAMED IMPORTS ONLY (mirror color.ts / RESEARCH.md Pitfall 3 — wildcard imports
 * defeat culori tree-shaking).
 *
 * ESLint `no-hex-colors` is disabled for `packages/design-system/src/runtime/**`
 * — the constant swatches (brass, cream, ink, on-panel danger) are raw hex by
 * design, exactly as the mobile constants are.
 */
import { converter, formatHex } from "culori";

const toHsl = converter("hsl");

/** A domain accent pair: low-chroma tint background + saturated foreground. */
export interface DomainHue {
  /** Tint background (chip fill). */
  tint: string;
  /** Foreground (icon/label) that reads on the tint. */
  fg: string;
}

/** The full derived Majlis token set for one brand + one theme (light/dark). */
export interface MajlisPalette {
  /** Money-panel base green (brand hue + 135°, S 0.50, L 0.21 +dark-lift). */
  payPanel: string;
  /** Money-panel deep stop (same hue, L 0.16 +dark-lift). */
  payPanelDeep: string;
  /** Cream on-panel text — fixed warm off-white, reads on any panel hue. */
  onPayPanel: string;
  /** Muted on-panel text — low-sat tint of the panel hue (shifts with brand). */
  onPayPanelMuted: string;
  /** Deduction/negative color on the dark money panel — fixed light coral. */
  onPayPanelDanger: string;
  domainLeaves: DomainHue;
  domainOvertime: DomainHue;
  /** Pay domain reuses the (green) panel hue so the salary pill shares identity. */
  domainPay: DomainHue;
  domainExpense: DomainHue;
}

/**
 * Constant money-metal accents + Iwan chrome — NOT brand-derived (mobile pins
 * these as `static const` on MajlisPalette / LinkHRTheme). Exported so the token
 * writer and tests reference one source of truth.
 */
export const MAJLIS_CONSTANTS = {
  /** Brass — money-metal accent; reads on paper AND on dark panels. */
  brass: "#D9A441",
  /** Brass deep — pressed/hover metal (web-only; darker sibling of brass). */
  brassDeep: "#B9862F",
  /** Cream on-panel text (mobile `onPayPanel`). */
  onPayPanel: "#F4EFE4",
  /** Deduction coral on the dark panel (mobile `onPayPanelDanger`). */
  onPayPanelDanger: "#EFA48F",
  /** Iwan ink chrome constant (Riwaq). */
  iwanInk: "#221C15",
  /** Iwan cream chrome constant (Riwaq). */
  iwanCream: "#FAF6EE",
} as const;

function norm(hue: number): number {
  return ((hue % 360) + 360) % 360;
}

/** HSL → hex via culori (h in degrees, s/l in 0..1). Never returns null. */
function hsl(h: number, s: number, l: number): string {
  return formatHex({ mode: "hsl", h: norm(h), s, l }) ?? "#000000";
}

/**
 * A per-domain accent pair (mobile `MajlisPalette._domain`). `fgSat`/`fgLight`
 * default to the standard recipe (S .50 / L .32 light); a domain whose hue
 * over/under-saturates at the default (expense at +292°) overrides them. The
 * dark-mode fg tracks `fgSat` (×0.9) but always lifts L to 0.72 so it reads on
 * the dark tint.
 */
function domain(hue: number, dark: boolean, fgSat = 0.5, fgLight = 0.32): DomainHue {
  if (dark) {
    return {
      tint: hsl(hue, 0.25, 0.16),
      fg: hsl(hue, Math.min(Math.max(fgSat * 0.9, 0), 1), 0.72),
    };
  }
  // Light tint L is 0.93, a +0.03 clamp off mobile's 0.90 (within the ±6 L/S
  // tolerance discipline). At 0.90 the two green domains (leaves fg #297A6A and
  // pay-domain fg #297A56, both pinned to mobile by A1) land at 4.24 / 4.31 — a
  // hair under the 4.5:1 text gate. The brief mandates clamping the RECIPE
  // (not the test) when the DEFAULT brand fails; the fgs stay pinned, only the
  // tint lifts, so all four domains clear ≥4.5:1 while preserving fg fidelity.
  return {
    tint: hsl(hue, 0.25, 0.93),
    fg: hsl(hue, fgSat, fgLight),
  };
}

/**
 * Derives the full Majlis palette from a tenant brand hex for one theme.
 * Mirrors `MajlisPalette.from(brand, brightness)` in mobile hsl_brand.dart.
 *
 * Returns `null` for unparseable input so callers can fall back rather than
 * emitting `NaN`-hued colors (parity with `parseHex` null-check discipline).
 */
export function deriveMajlisPalette(brandHex: string, dark: boolean): MajlisPalette | null {
  const parsed = toHsl(brandHex);
  if (!parsed) return null;
  // Achromatic brands (gray/black/white) parse to `h: undefined` in culori.
  // Flutter's `HSLColor.fromColor` returns hue 0 for achromatic colors, so we
  // substitute 0 here to MATCH the mobile TRUTH SOURCE — the hue rotations
  // (+135°/+150°/+18°/+292°) then derive a deterministic muted palette rather
  // than bailing to `null` (which left NO Majlis vars, and — worse — let a
  // prior colorful brand's stale inline vars survive a gray re-set).
  const h = parsed.h ?? 0;

  // Pay panel: brand hue + 135° lands the board's deep money-green; S 0.50;
  // dark mode lifts L by 0.04 so the panel separates from dark paper.
  const panelHue = norm(h + 135);
  const panelSat = 0.5;
  const lift = dark ? 0.04 : 0.0;

  return {
    payPanel: hsl(panelHue, panelSat, 0.21 + lift),
    payPanelDeep: hsl(panelHue, panelSat, 0.16 + lift),
    onPayPanel: MAJLIS_CONSTANTS.onPayPanel,
    onPayPanelMuted: hsl(panelHue, 0.2, 0.85),
    onPayPanelDanger: MAJLIS_CONSTANTS.onPayPanelDanger,
    // Leaves = brand +150° → teal-green fg (#297A6A on the default).
    domainLeaves: domain(norm(h + 150), dark),
    // Overtime = brand +18° → amber fg (#7A5A29 on the default).
    domainOvertime: domain(norm(h + 18), dark),
    // Pay domain reuses the green panel hue so the salary pill shares identity.
    domainPay: domain(panelHue, dark),
    // Expense = brand +292° → muted plum; fg override S .25 / L .39 avoids the
    // magenta the standard recipe produces at this hue (#7C4B74 on the default).
    domainExpense: domain(norm(h + 292), dark, 0.25, 0.39),
  };
}

/**
 * Flattens a derived palette + the constants into the CSS custom-property map
 * for one theme. The `suffix` distinguishes the light set (`""`) from the dark
 * set (`"-dark"`) so both can be written to `documentElement` at once and the
 * dark stylesheet can alias the semantic names onto the `-dark` variants
 * (setTenantPrimary writes INLINE vars, which outrank the `[data-theme=dark]`
 * stylesheet — so a plain single-set write would leak light values into dark).
 *
 * Constants (brass, cream, ink, on-panel danger) are theme-invariant — emitted
 * only in the unsuffixed set.
 */
export function majlisCssVars(
  palette: MajlisPalette,
  suffix: "" | "-dark",
): Record<string, string> {
  const vars: Record<string, string> = {
    [`--majlis-pay${suffix}`]: palette.payPanel,
    [`--majlis-pay-deep${suffix}`]: palette.payPanelDeep,
    [`--majlis-on-pay${suffix}`]: palette.onPayPanel,
    [`--majlis-on-pay-muted${suffix}`]: palette.onPayPanelMuted,
    [`--majlis-on-pay-danger${suffix}`]: palette.onPayPanelDanger,
    [`--majlis-leaves-tint${suffix}`]: palette.domainLeaves.tint,
    [`--majlis-leaves-fg${suffix}`]: palette.domainLeaves.fg,
    [`--majlis-overtime-tint${suffix}`]: palette.domainOvertime.tint,
    [`--majlis-overtime-fg${suffix}`]: palette.domainOvertime.fg,
    [`--majlis-pay-tint${suffix}`]: palette.domainPay.tint,
    [`--majlis-pay-fg${suffix}`]: palette.domainPay.fg,
    [`--majlis-expense-tint${suffix}`]: palette.domainExpense.tint,
    [`--majlis-expense-fg${suffix}`]: palette.domainExpense.fg,
  };

  // Theme-invariant chrome constants — only in the light (unsuffixed) set.
  if (suffix === "") {
    vars["--brass"] = MAJLIS_CONSTANTS.brass;
    vars["--brass-deep"] = MAJLIS_CONSTANTS.brassDeep;
    vars["--iwan-ink"] = MAJLIS_CONSTANTS.iwanInk;
    vars["--iwan-cream"] = MAJLIS_CONSTANTS.iwanCream;
  }

  return vars;
}

/**
 * The complete light+dark Majlis custom-property map for a brand hex, ready to
 * merge into `setTenantPrimary`'s DOM write. Returns `{}` for invalid input.
 */
export function deriveMajlisCssVars(brandHex: string): Record<string, string> {
  const light = deriveMajlisPalette(brandHex, false);
  const dark = deriveMajlisPalette(brandHex, true);
  if (!light || !dark) return {};
  return { ...majlisCssVars(light, ""), ...majlisCssVars(dark, "-dark") };
}
